<script setup>
import Chart from 'chart.js/auto'
import { ref, watch } from 'vue'
import CoordinateValue from '@/components/Global/CoordinateValue.vue'

const chartRef = ref(null)
let lineProfileChart = null

const props = defineProps({
  yAxisData: { type: Array, required: true },
  xAxisLength: { type: Number, default: null },
  startCoords: { type: Array, default: null },
  endCoords: { type: Array, default: null },
  positionAngle: { type: Number, default: null },
})

watch(() => [props.yAxisData, props.xAxisLength], () => {
  lineProfileChart ? updateChart() : createChart()
})

function createChart (){
  // chartJs can't use css vars as strings, so we need to get the actual value
  var style = getComputedStyle(document.body)
  const text = style.getPropertyValue('--text')
  const primary = style.getPropertyValue('--primary-interactive')
  const secondary = style.getPropertyValue('--secondary-interactive')
  const background = style.getPropertyValue('--secondary-background')

  lineProfileChart = new Chart(chartRef.value, {
    type: 'line',
    data: {
      labels: generateLabels(),
      datasets: [
        { // Flux value along the drawn line as a line plot
          label: 'Flux',
          data: props.yAxisData,
          // Line styling
          borderColor: primary,
          borderWidth: 2,
          borderJoinStyle: 'round',
          backgroundColor: primary,
          // Point styling
          pointRadius: 0,
          // Point hover styling
          pointHitRadius: 10,
          pointHoverBorderColor: secondary,
          pointHoverBackgroundColor: secondary,
        },
      ],
    },
    options: {
      scales: {
        x: { // Autoskip labels if there are too many to display
          title: { display: true, text: distanceLabel(), color: text },
          border: { color: text, width: 2 },
          ticks: { color: text, autoSkip: true, autoSkipPadding: 10 , maxRotation: 0 },
          grid: { color: background },
        },
        y: {
          title: { display: true, text: 'Luminosity', color: text },
          border: { color: text, width: 2 },
          ticks: { color: text, autoSkip: true, },
          grid: { color: background },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: { mode: 'index', intersect: false },
      },
      hover: { // Permissive hover, shows tooltips when cursor is near a point
        mode: 'index',
        intersect: false,
      }
    },
  })
}

function updateChart(){
  // Set the changed data
  lineProfileChart.data.labels = generateLabels()
  lineProfileChart.data.datasets[0].data = props.yAxisData
  lineProfileChart.options.scales.x.title.text = distanceLabel()
  // Call Chart.js update method
  lineProfileChart.update()
}
// Uses length of the line to generate step size in arcseconds or pixels as x-axis labels
function generateLabels() {
  const length = props.xAxisLength
  const data = props.yAxisData
  
  if (!length) {
    // If image has no pixScale or WCS we don't have the arcsec length, use data to find pixel length
    return Array.from({ length: data.length }, (_, i) => i)
  }

  const step = length / (data.length - 1)
  return Array.from({ length: data.length }, (_, i) => (i * step).toFixed(0))
}

function distanceLabel(){
  if(props.xAxisLength){ 
    return (props.xAxisLength.toFixed(6) + ' Arcseconds') 
  }
  else { 
    return (props.yAxisData.length + ' Pixels')
  }
}
</script>

<template>
  <div>
    <canvas
      ref="chartRef"
      class="line-plot"
    />
    <div class="line-details">
      <template v-if="startCoords && endCoords">
        <p>PRECISE ASTROMETRIC COORDINATES</p>
        <p>
          <b>Start:</b>
          RA
          <coordinate-value
            :value="startCoords[0]"
            axis="ra"
          />
          Dec
          <coordinate-value
            :value="startCoords[1]"
            axis="dec"
          />
        </p>
        <p>
          <b>End:</b>
          RA
          <coordinate-value
            :value="endCoords[0]"
            axis="ra"
          />
          Dec
          <coordinate-value
            :value="endCoords[1]"
            axis="dec"
          />
        </p>
      </template>
      <p v-if="positionAngle">
        <v-tooltip
          activator="parent"
          text="Angle showing how a celestial object is rotated in the sky"
        />
        <b>Position Angle:</b> {{ positionAngle.toFixed(3) }}° East of North
      </p>
    </div>
  </div>
</template>

<style scoped>
.line-details {
  margin-top: 1rem;
}
.line-details p {
  font-size: medium;
}
</style>
